import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import NoteAddIcon from '@mui/icons-material/NoteAdd';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: 'var(--al-surface)',
    color: 'white',
    borderRadius: 0,
    minWidth: '400px',
    maxWidth: '500px',
    border: '1px solid rgba(var(--al-primary-rgb), 0.3)'
  }
}));

const StyledDialogTitle = styled(DialogTitle)({
  backgroundColor: 'var(--al-bg)',
  color: 'var(--al-primary)',
  fontFamily: "'Montserrat', sans-serif",
  fontWeight: 700,
  fontSize: '0.9rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
});

const StyledDialogContent = styled(DialogContent)({
  padding: '20px',
  backgroundColor: 'var(--al-surface)'
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#363636',
    color: 'white',
    borderRadius: 0,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: '0.85rem',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)'
    },
    '&:hover fieldset': {
      borderColor: 'rgba(var(--al-primary-rgb), 0.5)'
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--al-primary)'
    }
  },
  '& .MuiInputBase-input': {
    color: 'white',
    lineHeight: 1.6
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.5)',
    '&.Mui-focused': {
      color: 'var(--al-primary)'
    }
  }
});

const StyledDialogActions = styled(DialogActions)({
  backgroundColor: 'var(--al-bg)',
  padding: '12px 20px',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  gap: '8px'
});

const SaveButton = styled(Button)({
  backgroundColor: 'var(--al-primary)',
  color: 'var(--al-bg)',
  fontWeight: 600,
  textTransform: 'uppercase',
  borderRadius: 0,
  padding: '8px 20px',
  fontFamily: "'Montserrat', sans-serif",
  fontSize: '0.75rem',
  '&:hover': {
    backgroundColor: '#7FE87F'
  },
  '&:disabled': {
    backgroundColor: 'rgba(var(--al-primary-rgb), 0.3)',
    color: 'rgba(0, 0, 0, 0.5)'
  }
});

const CancelButton = styled(Button)({
  color: 'rgba(255, 255, 255, 0.7)',
  textTransform: 'uppercase',
  borderRadius: 0,
  padding: '8px 16px',
  fontFamily: "'Montserrat', sans-serif",
  fontSize: '0.75rem',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  }
});

const ClearButton = styled(Button)({
  color: '#FF6B6B',
  textTransform: 'uppercase',
  borderRadius: 0,
  padding: '8px 16px',
  fontFamily: "'Montserrat', sans-serif",
  fontSize: '0.75rem',
  marginRight: 'auto',
  '&:hover': {
    backgroundColor: 'rgba(255, 107, 107, 0.1)'
  }
});

const InkNotesModal = ({ isOpen, onClose, inkNotes, onSave, designName }) => {
  const [notes, setNotes] = useState(inkNotes || '');

  // Sync with prop changes
  useEffect(() => {
    setNotes(inkNotes || '');
  }, [inkNotes, isOpen]);

  const handleSave = () => {
    onSave(notes);
    onClose();
  };

  const handleClear = () => {
    setNotes('');
  };

  const hasChanges = notes !== (inkNotes || '');

  return (
    <StyledDialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NoteAddIcon sx={{ fontSize: '1.2rem' }} />
          Ink Notes
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'rgba(255, 255, 255, 0.5)',
            padding: '4px',
            '&:hover': { color: 'var(--al-primary)' }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {designName && (
          <Typography 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              fontSize: '0.8rem', 
              mb: 2,
              textTransform: 'uppercase'
            }}
          >
            Design: <span style={{ color: 'var(--al-primary)' }}>{designName}</span>
          </Typography>
        )}

        <StyledTextField
          fullWidth
          multiline
          rows={4}
          placeholder="Add special ink instructions for printers..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          variant="outlined"
        />

        {notes && (
          <Typography 
            sx={{ 
              mt: 1, 
              color: 'rgba(255, 255, 255, 0.4)', 
              fontSize: '0.7rem' 
            }}
          >
            {notes.length} characters
          </Typography>
        )}
      </StyledDialogContent>

      <StyledDialogActions>
        {inkNotes && (
          <ClearButton onClick={handleClear}>
            Clear Notes
          </ClearButton>
        )}
        <CancelButton onClick={onClose}>
          Cancel
        </CancelButton>
        <SaveButton onClick={handleSave} disabled={!hasChanges}>
          {inkNotes ? 'Update Notes' : 'Save Notes'}
        </SaveButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default InkNotesModal;

